import { SubscriptionDocumentSignatureType } from '@/__generated__/graphql';
import { ElectronicSignatureIcon } from '@/assets/icons/electronicSignature';
import { WetSignatureIcon } from '@/assets/icons/wetSignature';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { Loader } from '@/components/loaders/loader';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { EXECUTE_ACTION_MUTATION } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface ReviewSignaturePacketProps {
  isOpen: boolean;
  currentRow: AllocationWithUpdated;
  onClose: () => void;
}

const ReviewSignaturePacket = ({ isOpen, currentRow, onClose }: ReviewSignaturePacketProps) => {
  const [getRewiewUrlLoading, setGetRewiewUrlLoading] = useState<boolean>(false);

  const [executeAction] = useMutation(EXECUTE_ACTION_MUTATION, {
    fetchPolicy: 'no-cache'
  });

  const openReviewUrl = async () => {
    if (!currentRow) return;

    try {
      setGetRewiewUrlLoading(true);

      const { data } = await executeAction({
        variables: {
          data: {
            allocationId: currentRow.id,
            action: 'reviewSignaturePacket',
            actionDataPayload: {
              subscriptionDocumentId: currentRow.subscriptionDocument.id
            }
          }
        }
      });

      const url = data.executeAction.parsedResponse;
      window.open(url, '_self');
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setGetRewiewUrlLoading(false);
    }
  };

  if (getRewiewUrlLoading) {
    return (
      <ModalWrappedContent isOpen={isOpen} onClose={onClose} showCancelButton={false} showCloseIcon={false} isUseOutsideClick={false}>
        <ModalBody alignItems="center">
          {currentRow.subscriptionDocument.signatureType === SubscriptionDocumentSignatureType.ESignature && (
            <>
              <ElectronicSignatureIcon />
              <ModalTitle>Electronic Signatures</ModalTitle>
              <BannerDescription>Please wait as DocuSign prepares documents.</BannerDescription>
              <Loader />
            </>
          )}
          {currentRow.subscriptionDocument.signatureType === SubscriptionDocumentSignatureType.WetSignature && (
            <>
              <WetSignatureIcon />
              <ModalTitle>Wet Signatures</ModalTitle>
              <BannerDescription>Please wait as DocuSign prepares documents.</BannerDescription>
              <Loader />
            </>
          )}
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      title="Send for Signature"
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{ name: 'Open in Docusign', onClick: openReviewUrl }}
    >
      <ModalBody>
        {currentRow.subscriptionDocument.signatureType === SubscriptionDocumentSignatureType.ESignature && (
          <BannerWrapper>
            <div>
              <ElectronicSignatureIcon />
            </div>
            <BannerContent>
              <BannerTitle>Electronic Signature</BannerTitle>
              <BannerDescription>Review, make any changes, and approve the Docusign before sending it to other recipients.</BannerDescription>
            </BannerContent>
          </BannerWrapper>
        )}
        {currentRow.subscriptionDocument.signatureType === SubscriptionDocumentSignatureType.WetSignature && (
          <BannerWrapper>
            <div>
              <WetSignatureIcon />
            </div>
            <BannerContent>
              <BannerTitle>Wet Signature</BannerTitle>
              <BannerDescription>
                In order to process a wet signature, first review, make any changes, and approve the signature packet in Docusign. No signatories will
                receive the Docusign. Instead, you will download the approved signature packet, collect signatures manually, and upload the document
                once it is fully executed.
              </BannerDescription>
            </BannerContent>
          </BannerWrapper>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default ReviewSignaturePacket;

const ModalBody = styled.div<{ alignItems?: string }>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems || 'flex-start'};
  flex-direction: column;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const BannerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 20px;
  border-left: 1px solid ${themeVar('border.base')};
`;

const BannerTitle = styled.div`
  font-size: 19px;
  font-weight: 700;
  line-height: 25px;
  color: ${themeVar('font.strong')};
`;

const BannerDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${themeVar('font.base')};
`;
